import { useNavigate } from 'react-router-dom';
import { useSettings } from '@/contexts/SettingsContext';

export function PrivacyPolicy() {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const language = settings?.language || 'en';
  const fr = language === 'fr';

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 border-b border-slate-200 bg-white/95 backdrop-blur-xl dark:border-slate-700 dark:bg-slate-900/95">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3 sm:px-6">
          <button onClick={() => navigate('/')} className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-purple-600">
              <span className="text-lg">💰</span>
            </div>
            <span className="font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              MyBalance
            </span>
          </button>
          <button
            onClick={() => navigate(-1)}
            className="text-sm font-medium text-slate-600 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-400 transition-colors"
          >
            {fr ? '← Retour' : '← Back'}
          </button>
        </div>
      </nav>

      <main className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="prose prose-slate dark:prose-invert max-w-none">
          <h1 className="text-3xl font-black text-slate-900 dark:text-white sm:text-4xl mb-2">
            {fr ? 'Politique de confidentialité' : 'Privacy Policy'}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-10">
            {fr ? 'Dernière mise à jour : Février 2026' : 'Last updated: February 2026'}
          </p>

          <div className="space-y-8 text-slate-700 dark:text-slate-300">

            <section>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
                {fr ? '1. Qui sommes-nous' : '1. Who we are'}
              </h2>
              <p className="leading-relaxed">
                {fr
                  ? <>MyBalance est une application de suivi des finances personnelles développée et exploitée par <strong>Unova</strong>. Nous nous engageons à protéger vos informations personnelles et votre droit à la vie privée.</>
                  : <>MyBalance is a personal finance tracking application developed and operated by <strong>Unova</strong>. We are committed to protecting your personal information and your right to privacy.</>
                }
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
                {fr ? '2. Quelles données nous collectons' : '2. What data we collect'}
              </h2>
              <p className="leading-relaxed mb-3">
                {fr ? 'Nous collectons uniquement ce qui est strictement nécessaire pour fournir le service :' : 'We collect only what is strictly necessary to provide the service:'}
              </p>
              <ul className="list-disc list-inside space-y-2 text-sm">
                {fr ? <>
                  <li><strong>Informations de compte :</strong> votre adresse e-mail et nom d'affichage lors de l'inscription.</li>
                  <li><strong>Données financières :</strong> transactions de revenus et de dépenses, catégories, montants, dates et notes que vous saisissez manuellement.</li>
                  <li><strong>Paramètres de l'application :</strong> préférence de devise, langue, thème et objectifs d'épargne.</li>
                  <li><strong>Données d'utilisation :</strong> analyses anonymes de base pour améliorer le produit (aucune donnée personnellement identifiable).</li>
                </> : <>
                  <li><strong>Account information:</strong> Your email address and display name when you register.</li>
                  <li><strong>Financial data:</strong> Income and expense transactions, categories, amounts, dates, and notes that you enter manually.</li>
                  <li><strong>App settings:</strong> Currency preference, language, theme, and savings goals.</li>
                  <li><strong>Usage data:</strong> Basic anonymous analytics to improve the product (no personally identifiable data).</li>
                </>}
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
                {fr ? '3. Comment nous utilisons vos données' : '3. How we use your data'}
              </h2>
              <ul className="list-disc list-inside space-y-2 text-sm">
                {fr ? <>
                  <li>Pour fournir, maintenir et améliorer le service MyBalance.</li>
                  <li>Pour authentifier votre identité et protéger votre compte.</li>
                  <li>Pour synchroniser vos données sur tous vos appareils.</li>
                  <li>Pour envoyer des e-mails transactionnels (ex : réinitialisation du mot de passe). Nous n'enverrons jamais d'e-mails marketing sans votre consentement explicite.</li>
                </> : <>
                  <li>To provide, maintain, and improve the MyBalance service.</li>
                  <li>To authenticate your identity and protect your account.</li>
                  <li>To sync your data across devices.</li>
                  <li>To send transactional emails (e.g., password reset). We will never send marketing emails without your explicit consent.</li>
                </>}
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
                {fr ? '4. Nous ne vendons jamais vos données' : '4. We do not sell your data - ever'}
              </h2>
              <p className="leading-relaxed">
                {fr
                  ? <>Vos données financières ne sont <strong>jamais vendues, louées, partagées ou monétisées</strong> de quelque manière que ce soit. Nous n'utilisons pas vos informations financières à des fins publicitaires. Vos données vous appartiennent.</>
                  : <>Your financial data is <strong>never sold, rented, shared, or monetized</strong> in any way. We do not use your financial information for advertising. Your data belongs to you.</>
                }
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
                {fr ? '5. Sécurité des données' : '5. Data security'}
              </h2>
              <p className="leading-relaxed mb-3">
                {fr ? 'Nous prenons la sécurité au sérieux et mettons en œuvre des protections de niveau bancaire :' : 'We take security seriously and implement bank-level protections:'}
              </p>
              <ul className="list-disc list-inside space-y-2 text-sm">
                {fr ? <>
                  <li>Toutes les données sont stockées dans Google Firebase avec chiffrement au repos et en transit (TLS 1.3).</li>
                  <li>Les règles de sécurité Firestore strictes garantissent que seul vous pouvez accéder à vos propres données.</li>
                  <li>HSTS, Content Security Policy et d'autres en-têtes de sécurité sont appliqués sur toutes les requêtes.</li>
                  <li>L'authentification par mot de passe utilise Firebase Auth avec hachage bcrypt. Nous ne stockons jamais les mots de passe en clair.</li>
                </> : <>
                  <li>All data is stored in Google Firebase with encryption at rest and in transit (TLS 1.3).</li>
                  <li>Strict Firestore security rules ensure only you can access your own data - no other user, and no admin, can read your transactions.</li>
                  <li>HTTP Strict Transport Security (HSTS), Content Security Policy, and other security headers are enforced on all requests.</li>
                  <li>Password authentication uses Firebase Auth with bcrypt hashing. We never store plaintext passwords.</li>
                </>}
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
                {fr ? '6. Stockage et localisation des données' : '6. Data storage and location'}
              </h2>
              <p className="leading-relaxed">
                {fr
                  ? "Vos données sont stockées sur l'infrastructure Google Firebase (Google Cloud). Firebase est conforme au RGPD et aux normes SOC 2/3. MyBalance est développé et exploité par Unova, une équipe basée en Inde et à Madagascar. Les données peuvent être stockées dans des centres de données Google Cloud à travers le monde."
                  : "Your data is stored on Google Firebase infrastructure (Google Cloud). Firebase complies with GDPR and SOC 2/3 standards. MyBalance is built and operated by Unova, a team based in India and Madagascar. Data may be stored in Google Cloud data centers globally."
                }
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
                {fr ? '7. Vos droits' : '7. Your rights'}
              </h2>
              <p className="leading-relaxed mb-3">
                {fr ? 'Vous avez le droit de :' : 'You have the right to:'}
              </p>
              <ul className="list-disc list-inside space-y-2 text-sm">
                {fr ? <>
                  <li><strong>Accès :</strong> exportez toutes vos données à tout moment via la fonction d'export intégrée (CSV ou Excel).</li>
                  <li><strong>Suppression :</strong> demandez la suppression complète de votre compte et de toutes les données associées en nous contactant.</li>
                  <li><strong>Portabilité :</strong> téléchargez vos données dans des formats standard à tout moment.</li>
                  <li><strong>Correction :</strong> modifiez ou supprimez toute transaction ou donnée dans l'application.</li>
                </> : <>
                  <li><strong>Access:</strong> Export all your data at any time using the built-in export feature (CSV or Excel).</li>
                  <li><strong>Deletion:</strong> Request complete deletion of your account and all associated data by contacting us.</li>
                  <li><strong>Portability:</strong> Download your data in standard formats at any time.</li>
                  <li><strong>Correction:</strong> Edit or delete any transaction or data point within the app.</li>
                </>}
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
                {fr ? '8. Services tiers' : '8. Third-party services'}
              </h2>
              <p className="leading-relaxed">
                {fr
                  ? <>MyBalance utilise les services tiers suivants : <strong>Google Firebase</strong> (authentification et base de données), <strong>Google OAuth</strong> (connexion optionnelle) et <strong>APIs de taux de change</strong> (données de devises publiques et anonymes — aucune donnée personnelle envoyée). Nous n'utilisons aucun SDK publicitaire, de suivi ou d'analyse.</>
                  : <>MyBalance uses the following third-party services: <strong>Google Firebase</strong> (authentication and database), <strong>Google OAuth</strong> (optional sign-in), and <strong>Exchange Rate APIs</strong> (public, anonymous currency data - no personal data sent). We use no advertising, tracking, or analytics SDKs.</>
                }
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
                {fr ? '9. Confidentialité des mineurs' : "9. Children's privacy"}
              </h2>
              <p className="leading-relaxed">
                {fr
                  ? "MyBalance n'est pas destiné aux enfants de moins de 13 ans. Nous ne collectons pas sciemment de données d'enfants de moins de 13 ans. Si vous pensez qu'un enfant de moins de 13 ans nous a fourni des données, contactez-nous et nous les supprimerons rapidement."
                  : "MyBalance is not directed to children under 13. We do not knowingly collect data from children under 13. If you believe a child under 13 has provided us data, please contact us and we will delete it promptly."
                }
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
                {fr ? '10. Modifications de cette politique' : '10. Changes to this policy'}
              </h2>
              <p className="leading-relaxed">
                {fr
                  ? "Nous pouvons mettre à jour cette politique occasionnellement. Nous vous informerons des changements significatifs par e-mail ou via une notification dans l'application. Votre utilisation continue de MyBalance après les modifications constitue une acceptation."
                  : "We may update this policy occasionally. We will notify you of significant changes via email or an in-app notification. Your continued use of MyBalance after changes constitutes acceptance."
                }
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
                {fr ? '11. Nous contacter' : '11. Contact us'}
              </h2>
              <p className="leading-relaxed">
                {fr ? 'Pour toute question relative à la confidentialité ou demande de suppression de données, contactez-nous à :' : 'For any privacy-related questions or data deletion requests, please contact us at:'}{' '}
                <a href="mailto:contact@mybalancya.com" className="text-blue-600 hover:underline dark:text-blue-400">
                  contact@mybalancya.com
                </a>
              </p>
            </section>

          </div>
        </div>
      </main>

      <footer className="border-t border-slate-200 dark:border-slate-700 mt-12 py-6 text-center text-xs text-slate-400">
        © 2026 Unova. {fr ? 'Tous droits réservés.' : 'All rights reserved.'}
      </footer>
    </div>
  );
}
